/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { negationOptions } from './SegmentCreatorItem';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryCondition, AQLQueryType, AQLQueryValue } from '@insights_module/adc_ql/types';
import { FilterInput } from '@insights_module/components/FilterInput';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

const getRangeCondition = (value: AQLQueryValue): string => {
  const keys = Object.keys(value as object);
  if (keys.length === 1 && keys[0] === 'gte') return 'greater_then_or_equal';
  if (keys.length === 1 && keys[0] === 'lte') return 'less_then_or_equal';
  return 'range';
};

export const SegmentCreatorReviewCountItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { type, negation, condition, value } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    {
      value: 'greater_then_or_equal',
      label: <Option>{t('insights.query_builder.conditions.greater_then_or_equal')}</Option>,
    },
    {
      value: 'less_then_or_equal',
      label: <Option>{t('insights.query_builder.conditions.less_then_or_equal')}</Option>,
    },
    { value: 'range', label: <Option>{t('insights.query_builder.conditions.range')}</Option> },
    { value: 'eq', label: <Option>{t('insights.query_builder.conditions.exact')}</Option> },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === type) || dropdownOptions[0]
  );
  const [isNegation, setIsNegation] = useState<boolean>(negation || false);
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find((item) => {
      if (condition === ('eq' as unknown as AQLQueryCondition) && item.value === 'eq') return true;
      if (
        condition === ('range' as unknown as AQLQueryCondition) &&
        item.value === getRangeCondition(value as AQLQueryValue)
      )
        return true;
    }) || conditionOptions[0]
  );

  const [lowValue, setLowValue] = useState<number | null>(
    // @ts-ignore
    condition === 'range' && typeof value === 'object' && value?.gte ? value?.gte : null
  );
  const [highValue, setHighValue] = useState<number | null>(
    // @ts-ignore
    condition === 'range' && typeof value === 'object' && value?.lte ? value?.lte : null
  );
  const [exactValue, setExactValue] = useState<number | null>(
    // @ts-ignore
    condition === 'eq' && typeof value === 'number' ? value : null
  );

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    setLowValue(null);
    setHighValue(null);
    setExactValue(null);
  }, [selectedCondition]);

  useEffect(() => {
    if (
      (selectedCondition && selectedCondition.value === 'eq' && exactValue) ||
      (selectedCondition.value === 'range' && lowValue && highValue) ||
      (selectedCondition.value === 'greater_then_or_equal' && lowValue) ||
      (selectedCondition.value === 'less_then_or_equal' && highValue)
    ) {
      const condition = 'range';
      const value =
        selectedCondition.value === 'eq'
          ? { gte: exactValue || undefined, lte: exactValue || undefined }
          : { gte: lowValue || undefined, lte: highValue || undefined };
      const segment = {
        ...segmentData,
        condition: condition as unknown as AQLQueryCondition,
        value: value,
        negation: isNegation,
      };
      pushSegmentData(segment);
    }
  }, [selectedCondition, lowValue, highValue, exactValue, isNegation]);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-review-filter-bg border border-spec-review-filter-stroke text-spec-review-filter-text font-semibold`}
        caretStyle={tw`fill-spec-review-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={true}
      />

      <Dropdown
        options={negationOptions}
        value={isNegation ? negationOptions[1] : negationOptions[0]}
        onChange={(val) => setIsNegation((val as IDropdownOption).value === 'is_not')}
        isSearchable={false}
        isPresentational={true}
      />

      <Dropdown
        options={conditionOptions}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isSearchable={false}
        dropdownMenuStyle={tw`w-[164px]`}
        isPresentational={true}
      />

      {selectedCondition?.value === 'eq' && (
        <FilterInput
          focus={!exactValue}
          type='number'
          placeholder={t('insights.query_builder.audio_count.placeholder')}
          value={(exactValue || '') as string}
          contentEditable={true}
          onPressEnter={(value) => setExactValue(value as number)}
        />
      )}

      {(selectedCondition.value === 'greater_then_or_equal' ||
        selectedCondition.value === 'range') && (
        <FilterInput
          focus={!lowValue}
          type='number'
          placeholder={t('insights.query_builder.audio_count.placeholder')}
          value={(lowValue || '') as string}
          contentEditable={true}
          onPressEnter={(value) => setLowValue(value as number)}
        />
      )}

      {(selectedCondition.value === 'less_then_or_equal' ||
        selectedCondition.value === 'range') && (
        <FilterInput
          focus={selectedCondition.value === 'less_then_or_equal' && !highValue}
          type='number'
          placeholder={t('insights.query_builder.audio_count.placeholder')}
          value={(highValue || '') as string}
          contentEditable={true}
          onPressEnter={(value) => setHighValue(value as number)}
        />
      )}
    </div>
  );
};
